import { useRouter } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { Dialog } from "radix-ui";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { plural, useWordsVersion } from "@/api/words";
import { formatCount } from "@/money/format";

import { uk } from "../i18n/uk";

import type { Menu } from "./api";
import { announceHashTarget } from "./hash-target";
import { MERGED_INTO, pathOf } from "./nav-paths";

// Поиск по экранам меню (F-013, решения 066 и 067): окно-палитра. Открывают поле в
// шапке справа, «Ctrl+K» и «/»; это будущий общий поиск по системе, пока он ищет экраны.
//
// ⚠⚠ Состав выдачи — меню человека, каким его отдал сервер: закрытого (`НВ`) в
// нём нет, значит нет и в поиске. Будущие экраны (решение 028) в выдаче стоят
// приглушёнными и не открываются — стрелки их пропускают, `Enter` на них не
// действует. «/» принадлежит полю поиска таблицы, если на экране оно есть
// (`useSlashFocus` гасит событие первым): палитра на уже обработанное «/» не
// откликается, а `Ctrl+K` открывает её всегда.

/** Одна строка выдачи: только то, что открывается (перечень готового — сервера). */
export interface SearchEntry {
  readonly code: string;
  readonly label: string;
  readonly group: string | null;
  readonly path: string;
}

/** «ОПЕРАЦІЇ» → «Операції»: в выдаче поиска группа обычным регистром (данные в настройке капсом — не трогаем). */
function sentence(text: string): string {
  const low = text.toLocaleLowerCase("uk-UA");
  return low.charAt(0).toLocaleUpperCase("uk-UA") + low.slice(1);
}

/** Строки выдачи из меню человека, каким его отдал сервер. */
export function entriesOf(menu: Menu): SearchEntry[] {
  const all = [
    ...menu.top.map((one) => ({ code: one.code, label: one.label_uk, group: null as string | null, path: pathOf(one.code) })),
    ...menu.groups.flatMap((group) =>
      group.items.map((one) => ({
        code: one.code,
        label: one.label_uk,
        group: sentence(group.name_uk ?? group.group) as string | null,
        path: pathOf(one.code),
      })),
    ),
  ];
  const entries: SearchEntry[] = [];
  for (const one of all) {
    if (one.path === undefined) console.error(`menu: у кода ${one.code} нет адреса в клиенте`);
    else entries.push({ ...one, path: one.path });
  }
  return entries;
}

function typing(target: EventTarget | null): boolean {
  const at = target as HTMLElement | null;
  return at !== null && (at.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(at.tagName));
}

/** Виден ли элемент: в закрытом меню узкого окна кнопка есть, но показать на ней фокус нельзя. */
function shown(at: HTMLElement | null): at is HTMLElement {
  if (at === null || !at.isConnected) return false;
  // Любой предок со `display: none` (закрытое меню узкого окна) прячет и сам элемент.
  for (let node: HTMLElement | null = at; node !== null; node = node.parentElement) {
    if (node.hidden || getComputedStyle(node).display === "none") return false;
  }
  return true;
}

/** Регистр и вид апострофа не мешают найти «Зобов'язання». */
function fold(text: string): string {
  return text.toLocaleLowerCase("uk-UA").replace(/[’ʼ]/g, "'");
}

/** Поле-кнопка в шапке и окно поиска. */
export function MenuSearch({
  entries,
  onGo,
}: {
  readonly entries: readonly SearchEntry[];
  /** Закрыть меню узкого экрана после перехода. */
  readonly onGo: () => void;
}) {
  useWordsVersion();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<string | null>(null);
  const listId = useId();
  // Откуда открыли: при закрытии фокус возвращается туда, а не на кнопку (Radix
  // возвращает на кнопку-триггер, даже когда окно открыли Ctrl+K из другого места).
  const opener = useRef<HTMLElement | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const isOpen = useRef(false);
  const leaving = useRef<"back" | "main">("back");
  const optionId = (code: string) => `${listId}-${code}`;

  // Горячие клавиши: окно открывают из любого места страницы.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const ctrlK = (event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === "k";
      const slash =
        event.key === "/" &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.defaultPrevented &&
        !typing(event.target) &&
        document.querySelector('[role="dialog"]') === null;
      if (!ctrlK && !slash) return;
      event.preventDefault();
      // Окно уже открыто: фокус сейчас в его поле, и «откуда открыли» перезаписывать нельзя.
      if (isOpen.current) return;
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      leaving.current = "back";
      isOpen.current = true;
      setOpen(true);
    };
    // Окно, а не документ: поле поиска таблицы слушает документ и гасит «/» раньше.
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const found = useMemo(() => {
    const needle = fold(query.trim());
    return entries.filter(
      (one) =>
        needle === "" || fold(one.label).includes(needle) || (one.group !== null && fold(one.group).includes(needle)),
    );
  }, [entries, query]);
  const current = found.find((one) => one.code === active) ?? found[0] ?? null;

  function openChange(next: boolean) {
    if (next && !open) {
      // Кнопкой в шапке: открывающий — сама кнопка (в Safari щелчок фокус на неё не ставит).
      opener.current = trigger.current;
      leaving.current = "back";
    }
    isOpen.current = next;
    setOpen(next);
    if (!next) {
      setQuery("");
      setActive(null);
    }
  }

  function go(entry: SearchEntry) {
    const merged = MERGED_INTO[entry.code];
    leaving.current = "main";
    openChange(false);
    onGo();
    void router.navigate({ to: entry.path as never, ...(merged === undefined ? {} : { hash: merged.hash }) });
    if (merged !== undefined) announceHashTarget(merged.hash);
  }

  function move(step: 1 | -1) {
    if (found.length === 0) return;
    const at = current === null ? -1 : found.indexOf(current);
    const next = found[(at + step + found.length) % found.length];
    if (next === undefined) return;
    setActive(next.code);
    document.getElementById(optionId(next.code))?.scrollIntoView?.({ block: "nearest" });
  }

  function onInputKey(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      move(event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (current !== null) go(current);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={openChange}>
      <Dialog.Trigger
        ref={trigger}
        aria-keyshortcuts="Control+K /"
        // Узкое окно — кнопка-значок 40×40, от 1280 — поле шириной 15rem с подсказкой клавиши.
        // Подсказку «/» в шапке не показываем: клавиша работает, а подпись только шумит.
        aria-label={uk.nav.search}
        className="flex size-control shrink-0 items-center justify-center gap-sm rounded-control border border-line-control bg-sunken text-sm text-ink-2 transition-colors hover:bg-overlay hover:text-ink xl:w-60 xl:justify-start xl:px-sm"
      >
        <Search aria-hidden="true" className="size-4 shrink-0 text-ink-3" />
        <span className="hidden min-w-0 flex-1 truncate text-left xl:inline">{uk.nav.search}</span>
        <kbd aria-hidden="true" className="hidden shrink-0 font-sans text-2xs text-ink-3 xl:inline">Ctrl+K</kbd>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[var(--z-popover)] bg-canvas/70" />
        <Dialog.Content
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            // Фокус — туда, откуда открыли; нет такого элемента или он скрыт — на `main`.
            // После перехода на экран — тоже на `main`: прежнее место уже не то.
            event.preventDefault();
            const back = leaving.current === "back" ? opener.current : null;
            (shown(back) ? back : document.getElementById("main"))?.focus();
          }}
          className="fixed top-[12vh] left-1/2 z-[var(--z-popover)] flex max-h-[70vh] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 flex-col overflow-hidden rounded-lg border border-line bg-overlay shadow-lg"
        >
          <Dialog.Title className="sr-only">{uk.nav.searchTitle}</Dialog.Title>
          {/* Кольцо фокуса — на всю строку поля: у самого поля оно срезалось бы краем окна. */}
          <div className="flex items-center gap-sm border-b border-line-subtle px-md focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-[var(--border-focus)]">
            <Search aria-hidden="true" className="size-4 shrink-0 text-ink-3" />
            <input
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={current === null ? undefined : optionId(current.code)}
              aria-label={uk.nav.searchTitle}
              autoComplete="off"
              value={query}
              placeholder={uk.nav.searchPlaceholder}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(null);
              }}
              onKeyDown={onInputKey}
              // Общее `:focus-visible` стоит вне слоя и перебивает утилиту: оставляем одно кольцо — на строке поля.
              style={{ outline: "none" }}
              className="h-12 min-w-0 flex-1 bg-transparent text-base text-ink placeholder:text-ink-3"
            />
          </div>
          <ul id={listId} role="listbox" aria-label={uk.nav.searchTitle} className="min-h-0 flex-1 overflow-y-auto p-2xs">
            {found.map((one) => {
              const on = current !== null && current.code === one.code;
              return (
                <li
                  key={one.code}
                  id={optionId(one.code)}
                  role="option"
                  aria-selected={on}
                  onMouseMove={() => {
                    if (!on) setActive(one.code);
                  }}
                  onClick={() => go(one)}
                  className={
                    "flex items-center gap-sm rounded-control px-sm py-xs text-sm " +
                    (on
                      ? "relative cursor-pointer bg-accent-wash text-ink before:absolute before:inset-y-1.5 before:left-0 before:w-0.5 before:rounded-full before:bg-accent-line"
                      : "cursor-pointer text-ink-2")
                  }
                >
                  <span className="min-w-0 flex-1 truncate">{one.label}</span>
                  {one.group === null ? null : (
                    <span className="shrink-0 text-2xs tracking-wide text-ink-3">
                      {one.group}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          {/* Постоянная область: текст подставляется в неё, а не рисуется вместе с ней. */}
          <p role="status" className={found.length === 0 ? "px-md py-lg text-center text-sm text-ink-3" : "sr-only"}>
            {found.length === 0
              ? uk.nav.searchEmpty
              : query.trim() === ""
                ? ""
                : plural(uk.nav.searchFound, found.length, formatCount(found.length))}
          </p>
          <div className="flex items-center gap-md border-t border-line-subtle px-md py-xs text-2xs text-ink-3">
            <span className="flex items-center gap-xs">
              <span className="flex gap-1">
                <kbd className="rounded border border-line-control px-1 leading-4">↑</kbd>
                <kbd className="rounded border border-line-control px-1 leading-4">↓</kbd>
              </span>
              {uk.nav.searchHintPick}
            </span>
            <span className="flex items-center gap-xs">
              <kbd className="rounded border border-line-control px-1 leading-4">Enter</kbd>
              {uk.nav.searchHintOpen}
            </span>
            <span className="flex items-center gap-xs">
              <kbd className="rounded border border-line-control px-1 leading-4">Esc</kbd>
              {uk.nav.searchHintClose}
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
